import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Alert,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    LinearProgress
} from '@mui/material';
import {
    CloudUpload,
    Refresh,
    Email,
    Warning,
    CheckCircle,
    Error,
    Info
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// =================== STYLED COMPONENTS ===================

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const StyledCard = styled(Card)(({ theme }) => ({
    maxWidth: 600,
    margin: '0 auto',
    marginTop: theme.spacing(4),
    boxShadow: theme.shadows[10],
}));

const IconBox = styled(Box)(({ theme }) => ({
    fontSize: '4rem',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
}));

// =================== COMPONENTE PRINCIPAL ===================

export default function LicenseExpiredPage() {
    const navigate = useNavigate();
    const [licenseError, setLicenseError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        // Carrega informações do erro de licença
        const errorData = localStorage.getItem("licenseError");
        if (errorData) {
            try {
                setLicenseError(JSON.parse(errorData));
            } catch (e) {
                console.error("Erro ao parsear licenseError:", e);
            }
        }
    }, []);

    // =================== FUNÇÕES ===================

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        setUploadError('');
        setUploadSuccess(false);

        if (file) {
            // Validação básica do arquivo
            if (!file.name.endsWith('.json')) {
                setUploadError('Por favor, selecione um arquivo .json válido');
                setSelectedFile(null);
                return;
            }

            if (file.size > 1024 * 1024) { // 1MB
                setUploadError('Arquivo muito grande. Máximo 1MB');
                setSelectedFile(null);
                return;
            }
        }
    };

    const handleUploadLicense = async () => {
        if (!selectedFile) {
            setUploadError('Selecione um arquivo de licença primeiro');
            return;
        }

        setIsUploading(true);
        setUploadError('');

        try {
            // Lê o arquivo JSON
            const fileContent = await readFileAsText(selectedFile);
            let licenseData;

            try {
                licenseData = JSON.parse(fileContent);
            } catch (e) {
                throw new Error('Arquivo JSON inválido');
            }

            // Valida estrutura básica
            if (!licenseData.signed_license || !licenseData.signed_license.data) {
                throw new Error('Estrutura de licença inválida');
            }

            // Envia para o servidor
            const formData = new FormData();
            formData.append('license_file', selectedFile);
            const response = await api.post('/admin/license-reload', formData)

            

            if (response.status === 200) {
                setUploadSuccess(true);
                setUploadError('');
                
                // Limpa erro de licença
                localStorage.removeItem('licenseError');
                
                // Mostra sucesso por 2 segundos e redireciona
                setTimeout(() => {
                    navigate('/dashboard');
                }, 2000);
            } else {
                throw new Error(response.data?.message || 'Erro ao atualizar licença');
            }

        } catch (error) {
            console.error('Erro no upload:', error);
            setUploadError(error.message || 'Erro ao processar arquivo de licença');
        } finally {
            setIsUploading(false);
        }
    };

    const handleRetry = async () => {
        setIsChecking(true);
        try {
            const response = await fetch((process.env.REACT_APP_API_URL || "__BACKEND_API__") + '/license-status');
            const status = await response.json();
            
            if (status && status.valid) {
                // Licença válida agora - limpa erro e volta
                localStorage.removeItem('licenseError');
                navigate('/dashboard');
            } else {
                setUploadError('Licença ainda está inválida. Faça upload de uma nova licença.');
            }
        } catch (error) {
            setUploadError('Erro ao verificar licença. Tente novamente.');
        } finally {
            setIsChecking(false);
        }
    };

    const handleContactSupport = () => {
        const subject = encodeURIComponent('Licença Expirada - Hefesto(Tools)');
        const body = encodeURIComponent(`
        Olá,

        Estou com problemas na licença do sistema Hefesto(Tools).

        Detalhes do erro:
        - Sistema: ${licenseError?.app || 'Hefesto(Tools)'}
        - Erro: ${licenseError?.error || 'Licença expirada'}
        - Horário: ${licenseError?.timestamp ? new Date(licenseError.timestamp).toLocaleString() : 'N/A'}

        Aguardo retorno para renovação da licença.

        Obrigado.
        `);
        
        window.open(`mailto:comercial@ipv7.com.br.com?subject=${subject}&body=${body}`);
    };

    // =================== FUNÇÃO AUXILIAR ===================

    const readFileAsText = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(new Error('Erro ao ler arquivo'));
            reader.readAsText(file);
        });
    };

    // =================== RENDER ===================

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            backgroundColor: '#f5f5f5', 
            py: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <StyledCard>
                <CardContent sx={{ p: 4 }}>
                    {/* Ícone e Título */}
                    <IconBox>
                        {uploadSuccess ? '✅' : '🚫'}
                    </IconBox>

                    <Typography variant="h4" component="h1" gutterBottom align="center" color="error">
                        {uploadSuccess ? 'Licença Atualizada!' : 'Licença Expirada'}
                    </Typography>

                    {uploadSuccess ? (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            <Typography variant="body1">
                                Licença atualizada com sucesso! Redirecionando...
                            </Typography>
                        </Alert>
                    ) : (
                        <>
                            <Typography variant="body1" color="text.secondary" paragraph align="center">
                                {licenseError?.message || "Sua licença expirou ou é inválida"}
                            </Typography>

                            {/* Detalhes do Erro */}
                            {licenseError && (
                                <Alert severity="warning" sx={{ mb: 3 }}>
                                    <Typography variant="body2">
                                        <strong>Detalhes:</strong><br />
                                        Sistema: {licenseError.app}<br />
                                        Erro: {licenseError.error}<br />
                                        Horário: {new Date(licenseError.timestamp).toLocaleString()}
                                    </Typography>
                                </Alert>
                            )}

                            {/* Upload de Licença */}
                            <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#fafafa' }}>
                                <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                    <CloudUpload sx={{ mr: 1 }} />
                                    Atualizar Licença
                                </Typography>
                                
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Faça upload do novo arquivo de licença (.json) fornecido pelo suporte:
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        startIcon={<CloudUpload />}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                    >
                                        {selectedFile ? selectedFile.name : 'Selecionar Arquivo de Licença'}
                                        <VisuallyHiddenInput
                                            type="file"
                                            accept=".json"
                                            onChange={handleFileSelect}
                                        />
                                    </Button>

                                    {selectedFile && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            <Typography variant="body2">
                                                Arquivo selecionado: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                                            </Typography>
                                        </Alert>
                                    )}

                                    <Button
                                        variant="contained"
                                        onClick={handleUploadLicense}
                                        disabled={!selectedFile || isUploading}
                                        fullWidth
                                        startIcon={isUploading ? <CircularProgress size={20} /> : <CheckCircle />}
                                    >
                                        {isUploading ? 'Atualizando Licença...' : 'Atualizar Licença'}
                                    </Button>
                                </Box>

                                {uploadError && (
                                    <Alert severity="error">
                                        {uploadError}
                                    </Alert>
                                )}
                            </Paper>

                            <Divider sx={{ my: 3 }}>OU</Divider>

                            {/* Ações */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleRetry}
                                    disabled={isChecking}
                                    startIcon={isChecking ? <CircularProgress size={20} /> : <Refresh />}
                                    sx={{ flex: 1 }}
                                >
                                    {isChecking ? 'Verificando...' : 'Tentar Novamente'}
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={handleContactSupport}
                                    startIcon={<Email />}
                                    color="success"
                                    sx={{ flex: 1 }}
                                >
                                    Contatar Suporte
                                </Button>
                            </Box>
                        </>
                    )}
                </CardContent>

                {/* Instruções */}
                {!uploadSuccess && (
                    <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 3, pt: 0 }}>
                        <Alert severity="info">
                            <Typography variant="body2">
                                <strong>Como resolver:</strong>
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><Info fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Faça upload do novo arquivo de licença" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Email fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Entre em contato com o suporte se não tiver o arquivo" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Refresh fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Verifique a conectividade com o servidor" />
                                </ListItem>
                            </List>
                        </Alert>
                    </CardActions>
                )}
            </StyledCard>
        </Box>
    );
}